import React, { useState, useRef, useCallback, useEffect, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from './ui/dialog';
import type { FieldConfiguration, AssignedUser } from '@/types/workflow';
import { CustomDropdownField } from './custom-dropdown-field';
import { defaultBooleanOptions } from './constants';
import { useToast } from '@/hooks/use-toast';
import { MultiSelectCell } from './multi-select-cell';
import { ModernDatePicker } from './modern-date-picker';
import { isBooleanField, isDropdownField, isTextListField, isDateField, isDateTimeField, isNumberField, isEmailField, matchesFieldFormatting, getSearchColumnPath, getFacetKeysFromConfiguration } from '@/utils/column-type-helpers';
import { isValidEmailFormat } from '@/utils/email-helpers';
import { getLocalizedField } from '@/utils/language-utils';
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking';
import { useWorkflow } from '@/context/workflow-context-simple';
import { useWorkflowApi } from '@/hooks/use-workflow-api';
import { useCodeValues } from '@/hooks/use-code-values';
import { useEntityType } from '@/contexts/entity-type-context';
import { Avatar, AvatarFallback, getInitials, getAvatarColor } from '@/components/ui/avatar';
import { Plus, X, Search, Loader2, Trash2, ChevronDown, Check, Building2 } from 'lucide-react';

// Context to provide the dialog element as a portal target for dropdowns
// This lets dropdowns render outside the scrollable body but inside the dialog (above overlay)
const DropdownPortalCtx = createContext<React.RefObject<HTMLDivElement | null>>({ current: null });

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: string[];
  bulkUpdateReports: (updates: { item: any; updates: Record<string, any> }[]) => Promise<any>;
  applyOptimisticUpdates: (updates: { id: string; updates: Record<string, any> }[]) => void;
  setSelectedItems: (items: string[]) => void;
  t: (key: string) => string;
  fields: FieldConfiguration[];
  language: string;
  assignedUsers: AssignedUser[];
  refreshData: (customFilterArray?: string[], orderBy?: string, facetKeys?: string[]) => void;
}

type BulkEditRow = { fieldConfigurationId: string; columnCode: string; value: string | string[] | null };

// ─── Inline User Search Picker for AssignedToUser ──────────────────
function UserSearchPicker({ value, onChange, placeholder, t }: {
  value: string | null;
  onChange: (user: { email: string; fullName: string; profileId?: string } | null) => void;
  placeholder?: string;
  t: (key: string) => string;
}) {
  const { searchUsers } = useWorkflowApi();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Array<{ email: string; fullName: string; profileId?: string }>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const portalContainerRef = useContext(DropdownPortalCtx);
  const pos = useDropdownPosition(inputContainerRef, portalContainerRef, isOpen);

  // Outside click handler — check both input area and portaled dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (inputContainerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Reset active index when results change
  useEffect(() => { setActiveIndex(-1); }, [results]);

  // Cleanup pending debounce on unmount
  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    requestIdRef.current++;
  }, []);

  const handleSearch = useCallback((text: string) => {
    setQuery(text);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Invalidate any in-flight request immediately so stale responses are ignored
    requestIdRef.current++;
    if (!text || text.trim().length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    timeoutRef.current = setTimeout(async () => {
      const id = ++requestIdRef.current;
      try {
        const res = await searchUsers(text.trim());
        if (id !== requestIdRef.current) return;
        setResults(res.map(u => ({ email: u.email, fullName: u.fullName || u.email, profileId: u.profileId })));
      } catch {
        if (id !== requestIdRef.current) return;
        setResults([]);
      } finally {
        if (id === requestIdRef.current) setIsSearching(false);
      }
    }, 400);
  }, [searchUsers]);

  const toTitleCase = (str: string) => str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());

  const handleSelect = (user: { email: string; fullName: string; profileId?: string }) => {
    setSelectedLabel(toTitleCase(user.fullName));
    setQuery('');
    setResults([]);
    setIsOpen(false);
    onChange(user);
  };

  const handleClear = () => {
    setSelectedLabel(null);
    setQuery('');
    setResults([]);
    onChange(null);
  };

  // If value changes externally, clear selected label
  useEffect(() => {
    if (!value) setSelectedLabel(null);
  }, [value]);

  const handleUserKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIndex >= 0 && results[activeIndex]) { e.preventDefault(); handleSelect(results[activeIndex]); }
    else if (e.key === 'Escape') { setIsOpen(false); }
  };

  const showDropdown = isOpen && (query.length > 0 || isSearching);
  const dropdownEl = showDropdown ? (
    <div
      ref={dropdownRef}
      className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
      style={{ position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
    >
      <div className="max-h-48 overflow-y-auto" role="listbox" id="user-search-listbox">
        {isSearching && (
          <div className="flex items-center gap-2 px-3 py-3 text-gray-400 text-sm">
            <Loader2 size={14} className="animate-spin" /> {t('searchingUsers') || 'Searching...'}
          </div>
        )}
        {!isSearching && query.trim().length >= 2 && results.length === 0 && (
          <div className="px-3 py-3 text-gray-400 text-sm">{t('noUsersFound') || 'No users found'}</div>
        )}
        {!isSearching && query.trim().length > 0 && query.trim().length < 2 && (
          <div className="px-3 py-3 text-gray-400 text-sm">{t('typeAtLeastTwoChars') || 'Type at least 2 characters...'}</div>
        )}
        {results.map((u, idx) => {
          const label = toTitleCase(u.fullName);
          const isActive = idx === activeIndex;
          return (
            <div
              key={u.email + '-' + idx}
              id={`user-option-${idx}`}
              role="option"
              aria-selected={isActive}
              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isActive ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleSelect(u); }}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <Avatar className="h-7 w-7 flex-shrink-0">
                <AvatarFallback bgColor={getAvatarColor(label)} initials={getInitials(label)} />
              </Avatar>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-gray-900 truncate">{label}</span>
                <span className="text-xs text-gray-400 truncate">{u.email}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div ref={inputContainerRef} className="relative">
      {/* Selected user chip or search input */}
      {selectedLabel && !isOpen ? (
        <div className="flex items-center gap-2 h-9 px-3 bg-gray-50 border border-gray-200 rounded-lg">
          <Avatar className="h-5 w-5 flex-shrink-0">
            <AvatarFallback bgColor={getAvatarColor(selectedLabel)} initials={getInitials(selectedLabel)} />
          </Avatar>
          <span className="text-sm text-gray-900 truncate flex-1">{selectedLabel}</span>
          <button type="button" onClick={handleClear} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            className="w-full h-9 pl-8 pr-3 text-sm bg-gray-50 rounded-lg border border-gray-200 outline-none focus:bg-white focus:border-gray-300 transition-colors placeholder:text-gray-400"
            placeholder={placeholder || 'Search users...'}
            value={query}
            onChange={e => { handleSearch(e.target.value); setIsOpen(true); }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleUserKeyDown}
            role="combobox"
            aria-expanded={showDropdown}
            aria-controls="user-search-listbox"
            aria-activedescendant={activeIndex >= 0 ? `user-option-${activeIndex}` : undefined}
          />
        </div>
      )}
      {dropdownEl && portalContainerRef.current
        ? createPortal(dropdownEl, portalContainerRef.current)
        : dropdownEl}
    </div>
  );
}

// ─── Inline Client Search Picker for the Client column ────────────
// Sibling of UserSearchPicker. The Client field is structurally an object
// ({ fboId, name, obCode }) rather than a freeform string, so we resolve it
// via the same `searchClientOrganizations` endpoint the inline grid cell
// uses. Keeping the visual shape identical to UserSearchPicker means the
// bulk-edit row looks the same whether you're swapping a user or a client.
function ClientSearchPicker({ value, onChange, placeholder, t }: {
  value: { fboId: number; name: string; obCode?: string } | null;
  onChange: (client: { fboId: number; name: string; obCode?: string } | null) => void;
  placeholder?: string;
  t: (key: string) => string;
}) {
  const { searchClientOrganizations } = useWorkflowApi();
  const [query, setQuery] = useState('');
  type ClientHit = { fboId: number; name: string; obCode?: string };
  const [results, setResults] = useState<ClientHit[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(value?.name ?? null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const portalContainerRef = useContext(DropdownPortalCtx);
  const pos = useDropdownPosition(inputContainerRef, portalContainerRef, isOpen);

  // Outside click handler — check both input area and portaled dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (inputContainerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Reset active index when results change
  useEffect(() => { setActiveIndex(-1); }, [results]);

  // Cleanup pending debounce on unmount
  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    requestIdRef.current++;
  }, []);

  const handleSearch = useCallback((text: string) => {
    setQuery(text);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Invalidate any in-flight request immediately so stale responses are ignored
    requestIdRef.current++;
    if (!text || text.trim().length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    timeoutRef.current = setTimeout(async () => {
      const id = ++requestIdRef.current;
      try {
        const res = await searchClientOrganizations(text.trim());
        if (id !== requestIdRef.current) return;
        setResults(res.map(o => ({
          fboId: o.entityId,
          name: o.displayName,
          obCode: o.organizationCode,
        })));
      } catch {
        if (id !== requestIdRef.current) return;
        setResults([]);
      } finally {
        if (id === requestIdRef.current) setIsSearching(false);
      }
    }, 400);
  }, [searchClientOrganizations]);

  const handleSelect = (client: ClientHit) => {
    setSelectedLabel(client.name);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    onChange(client);
  };

  const handleClear = () => {
    setSelectedLabel(null);
    setQuery('');
    setResults([]);
    onChange(null);
  };

  // If value changes externally, clear selected label
  useEffect(() => {
    if (!value) setSelectedLabel(null);
    else setSelectedLabel(value.name);
  }, [value]);

  const handleClientKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIndex >= 0 && results[activeIndex]) { e.preventDefault(); handleSelect(results[activeIndex]); }
    else if (e.key === 'Escape') { setIsOpen(false); }
  };

  const showDropdown = isOpen && (query.length > 0 || isSearching);
  const dropdownEl = showDropdown ? (
    <div
      ref={dropdownRef}
      className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
      style={{ position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
    >
      <div className="max-h-48 overflow-y-auto" role="listbox" id="client-search-listbox">
        {isSearching && (
          <div className="flex items-center gap-2 px-3 py-3 text-gray-400 text-sm">
            <Loader2 size={14} className="animate-spin" /> {t('searchingUsers') || 'Searching...'}
          </div>
        )}
        {!isSearching && query.trim().length >= 2 && results.length === 0 && (
          <div className="px-3 py-3 text-gray-400 text-sm">{t('noOptionsAvailable') || 'No clients found'}</div>
        )}
        {!isSearching && query.trim().length > 0 && query.trim().length < 2 && (
          <div className="px-3 py-3 text-gray-400 text-sm">{t('typeAtLeastTwoChars') || 'Type at least 2 characters...'}</div>
        )}
        {results.map((c, idx) => {
          const isActive = idx === activeIndex;
          return (
            <div
              key={`${c.fboId}-${idx}`}
              id={`client-option-${idx}`}
              role="option"
              aria-selected={isActive}
              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isActive ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              title={c.obCode || c.name}
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleSelect(c); }}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <Building2 className="h-5 w-5 text-gray-500 flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-gray-900 truncate">{c.name}</span>
                {c.obCode && (
                  <span className="text-xs text-gray-400 truncate">{c.obCode}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div ref={inputContainerRef} className="relative">
      {/* Selected client chip or search input */}
      {selectedLabel && !isOpen ? (
        <div className="flex items-center gap-2 h-9 px-3 bg-gray-50 border border-gray-200 rounded-lg">
          <Building2 className="h-5 w-5 text-gray-500 flex-shrink-0" />
          <span className="text-sm text-gray-900 truncate flex-1">{selectedLabel}</span>
          <button type="button" onClick={handleClear} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            className="w-full h-9 pl-8 pr-3 text-sm bg-gray-50 rounded-lg border border-gray-200 outline-none focus:bg-white focus:border-gray-300 transition-colors placeholder:text-gray-400"
            placeholder={placeholder || t('vmsSearchClient') || 'Search by client name...'}
            value={query}
            onChange={e => { handleSearch(e.target.value); setIsOpen(true); }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleClientKeyDown}
            role="combobox"
            aria-expanded={showDropdown}
            aria-controls="client-search-listbox"
            aria-activedescendant={activeIndex >= 0 ? `client-option-${activeIndex}` : undefined}
          />
        </div>
      )}
      {dropdownEl && portalContainerRef.current
        ? createPortal(dropdownEl, portalContainerRef.current)
        : dropdownEl}
    </div>
  );
}

// ─── Helper: compute position relative to a portal container ──────
function useDropdownPosition(buttonRef: React.RefObject<HTMLElement | null>, portalRef: React.RefObject<HTMLElement | null>, isOpen: boolean) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  useEffect(() => {
    if (!isOpen || !buttonRef.current || !portalRef.current) return;
    const btnRect = buttonRef.current.getBoundingClientRect();
    const containerRect = portalRef.current.getBoundingClientRect();
    setPos({
      top: btnRect.bottom - containerRect.top + 4,
      left: btnRect.left - containerRect.left,
      width: btnRect.width,
    });
  }, [isOpen, buttonRef, portalRef]);
  return pos;
}

// ─── Searchable Field Selector ─────────────────────────────────────
function FieldSelector({ value, options, onChange, placeholder, language }: {
  value: string;
  options: FieldConfiguration[];
  onChange: (fieldId: string) => void;
  placeholder?: string;
  language: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const portalContainerRef = useContext(DropdownPortalCtx);
  const pos = useDropdownPosition(buttonRef, portalContainerRef, isOpen);

  // Outside click handler — check both button and dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);
  // Reset active index when search changes
  useEffect(() => { setActiveIndex(-1); }, [search]);

  const selectedField = options.find(f => f.fieldConfigurationId === value);
  const selectedLabel = selectedField ? (language === 'fr' ? selectedField.columnNameFrench : selectedField.columnNameEnglish) : '';

  const filtered = options
    .filter(f => {
      if (!search) return true;
      const name = (language === 'fr' ? f.columnNameFrench : f.columnNameEnglish) || '';
      return name.toLowerCase().includes(search.toLowerCase());
    })
    .sort((a, b) => {
      const aName = (language === 'fr' ? a.columnNameFrench : a.columnNameEnglish) || '';
      const bName = (language === 'fr' ? b.columnNameFrench : b.columnNameEnglish) || '';
      return aName.localeCompare(bName);
    });

  const selectItem = (fieldId: string) => {
    onChange(fieldId);
    setIsOpen(false);
    setSearch('');
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIndex >= 0 && filtered[activeIndex]) { e.preventDefault(); selectItem(filtered[activeIndex].fieldConfigurationId); }
    else if (e.key === 'Escape') { setIsOpen(false); }
  };

  const dropdownEl = isOpen ? (
    <div
      ref={dropdownRef}
      className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
      style={{ position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
    >
      <div className="p-2 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            ref={inputRef}
            className="w-full h-7 pl-8 pr-3 text-sm bg-gray-50 rounded-md border border-gray-200 outline-none focus:bg-white focus:border-gray-300 transition-colors placeholder:text-gray-400"
            placeholder="Search fields..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-expanded={true}
            aria-controls="field-selector-listbox"
            aria-activedescendant={activeIndex >= 0 && filtered[activeIndex] ? `field-option-${filtered[activeIndex].fieldConfigurationId}` : undefined}
          />
        </div>
      </div>
      <div className="max-h-48 overflow-y-auto" role="listbox" id="field-selector-listbox">
        {filtered.length === 0 && (
          <div className="px-3 py-3 text-gray-400 text-sm">No fields found</div>
        )}
        {filtered.map((f, idx) => {
          const label = (language === 'fr' ? f.columnNameFrench : f.columnNameEnglish) || f.columnCode;
          const isSelected = f.fieldConfigurationId === value;
          const isActive = idx === activeIndex;
          return (
            <div
              key={f.fieldConfigurationId}
              id={`field-option-${f.fieldConfigurationId}`}
              role="option"
              aria-selected={isSelected}
              className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm transition-colors ${isActive ? 'bg-gray-100' : isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'}`}
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                selectItem(f.fieldConfigurationId);
              }}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <span className="truncate">{label}</span>
              {isSelected && <Check size={14} className="text-blue-600 flex-shrink-0" />}
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className="w-full h-9 px-3 text-sm bg-white border border-gray-200 rounded-lg outline-none hover:border-gray-300 transition-colors flex items-center justify-between gap-2 text-left"
        onClick={() => { setIsOpen(prev => !prev); setSearch(''); }}
      >
        <span className={selectedLabel ? 'text-gray-900 truncate' : 'text-gray-400 truncate'}>
          {selectedLabel || placeholder || 'Select field...'}
        </span>
        <ChevronDown size={14} className={`text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {dropdownEl && portalContainerRef.current
        ? createPortal(dropdownEl, portalContainerRef.current)
        : dropdownEl}
    </div>
  );
}

export const BulkEditModal: React.FC<BulkEditModalProps> = ({ isOpen, onClose, selectedItems, bulkUpdateReports, applyOptimisticUpdates, setSelectedItems, t, fields, language, assignedUsers, refreshData }) => {
  const [rows, setRows] = useState<BulkEditRow[]>([
    { fieldConfigurationId: '', columnCode: '', value: '' }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [multiSelectIdx, setMultiSelectIdx] = useState<number | null>(null);
  const rowKeyCounter = useRef(1);
  const [rowKeys, setRowKeys] = useState<number[]>([0]);
  const [removingKeys, setRemovingKeys] = useState<Set<number>>(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const removalTimersRef = useRef<Set<NodeJS.Timeout>>(new Set());
  const { toast } = useToast();
  const { trackWorkflowEvent } = useWorkflowTracking();
  const { configuration, currentStage, stageFilters, globalFilters, workOrders } = useWorkflow();
  const { entityType } = useEntityType();
  // CodeValueSource backing data — used by getDropdownOptions below for
  // fields whose options come from a tenant code-values table (currencies,
  // job titles, etc.) rather than the local customValues array on the field
  // config. Mirrors editable-cell.tsx so inline and bulk paths render the
  // same option list from the same source.
  const codeValues = useCodeValues();

  // Track selected user objects for AssignedToUser rows (by row index)
  const [selectedUserByRow, setSelectedUserByRow] = useState<Record<number, { email: string; fullName: string; profileId?: string }>>({});

  // Track selected client objects for Client rows (by row index). Mirrors
  // selectedUserByRow — the picker hands back a structured object but the
  // BulkEditRow.value field is a string/array/null, so we keep the full
  // client shape side-car here and use it when building the payload.
  const [selectedClientByRow, setSelectedClientByRow] = useState<Record<number, { fboId: number; name: string; obCode?: string }>>({});

  const handleAddRow = () => {
    const newKey = rowKeyCounter.current++;
    setRows([...rows, { fieldConfigurationId: '', columnCode: '', value: '' }]);
    setRowKeys(prev => [...prev, newKey]);
  };

  // Helper to reset rows to initial state
  const resetRows = () => {
    // Cancel any pending removal animations
    removalTimersRef.current.forEach(t => clearTimeout(t));
    removalTimersRef.current.clear();
    setRemovingKeys(new Set());
    rowKeyCounter.current = 1;
    setRows([{ fieldConfigurationId: '', columnCode: '', value: '' }]);
    setRowKeys([0]);
    setSelectedUserByRow({});
    setSelectedClientByRow({});
    setMultiSelectIdx(null);
  };

  const handleRemoveRow = (idx: number) => {
    if (rows.length === 1) {
      resetRows();
      return;
    }
    // Mark the row key as "removing" to trigger exit animation
    const keyToRemove = rowKeys[idx];
    setRemovingKeys(prev => new Set(prev).add(keyToRemove));
    // After animation completes, actually remove the row
    const timer = setTimeout(() => {
      removalTimersRef.current.delete(timer);
      setRemovingKeys(prev => { const next = new Set(prev); next.delete(keyToRemove); return next; });
      setRows(prev => prev.filter((_, i) => i !== idx));
      setRowKeys(prev => prev.filter((_, i) => i !== idx));
      // Re-index selected users
      const newMap: Record<number, { email: string; fullName: string; profileId?: string }> = {};
      Object.entries(selectedUserByRow).forEach(([key, val]) => {
        const k = parseInt(key);
        if (k < idx) newMap[k] = val;
        else if (k > idx) newMap[k - 1] = val;
      });
      setSelectedUserByRow(newMap);
      // Same re-index pass for selected clients
      const newClientMap: Record<number, { fboId: number; name: string; obCode?: string }> = {};
      Object.entries(selectedClientByRow).forEach(([key, val]) => {
        const k = parseInt(key);
        if (k < idx) newClientMap[k] = val;
        else if (k > idx) newClientMap[k - 1] = val;
      });
      setSelectedClientByRow(newClientMap);
    }, 200);
    removalTimersRef.current.add(timer);
  };

  const handleFieldChange = (idx: number, fieldId: string) => {
    const field = fields.find(f => f.fieldConfigurationId === fieldId);
    setRows(rows.map((row, i) =>
      i === idx
        ? { ...row, fieldConfigurationId: fieldId, columnCode: field ? field.columnCode : '', value: '' }
        : row
    ));
    // Clear any selected user for this row
    const newMap = { ...selectedUserByRow };
    delete newMap[idx];
    setSelectedUserByRow(newMap);
    // Clear any selected client for this row too — switching the field code
    // invalidates the previous structured pick regardless of type.
    const newClientMap = { ...selectedClientByRow };
    delete newClientMap[idx];
    setSelectedClientByRow(newClientMap);
  };

  const handleValueChange = (idx: number, value: string | string[] | null) => {
    setRows(rows.map((row, i) => (i === idx ? { ...row, value } : row)));
  };

  // Format date for saving - convert local midnight to UTC.
  // Expects a date-only 'YYYY-MM-DD' string. Anything else (e.g. a full ISO timestamp)
  // yields NaN segments and an Invalid Date, whose toISOString() THROWS RangeError — and
  // this runs while the bulk payload is being built, outside the submit try/catch, so a
  // throw here kills the submit silently. Return null instead of throwing; DateTime fields
  // are routed around this helper entirely (see the isDateTimeField branch in the payload build).
  const formatDateForSave = (dateStr: string | null) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    if (Number.isNaN(localDate.getTime())) return null;
    return localDate.toISOString();
  };

  // Helper to get dropdown options for a field (excluding AssignedToUser —
  // handled separately by UserSearchPicker).
  //
  // Two sources of options handled here:
  // (1) Field's local `customValues` array — tenant-managed per-field options
  //     stored on the field configuration itself.
  // (2) Field's `codeValueSource` — points at a global code-values table
  //     (currencies, job titles, etc.) cached by the useCodeValues hook.
  //     Same source the inline editable cell uses, so options stay in sync.
  //
  // (1) wins if both are set (unusual but defensive). When the codeValueSource
  // cache isn't ready yet we fall back to an empty list rather than blocking
  // the modal — the user can switch fields and come back, or the cache will
  // hydrate during the modal's lifetime.
  function getDropdownOptions(field: FieldConfiguration) {
    if (Array.isArray(field.customValues) && field.customValues.length > 0) {
      return field.customValues.map((opt, idx) => ({
        value: opt.code,
        label: language === 'fr' ? (opt.titleFrench || opt.code) : (opt.titleEnglish || opt.code),
        color: opt.color,
        key: `${opt.code}-${idx}`
      }));
    }
    if (field.codeValueSource && codeValues.isReady) {
      return codeValues
        .getCodeValuesSortByText(field.codeValueSource, false)
        .map((c: { code: string; text: string }, idx: number) => ({
          value: c.code,
          label: c.text,
          key: `${c.code}-${idx}`,
        }));
    }
    return [];
  }

  // Helper to get multi-select options for textList
  function getTextListOptions(field: FieldConfiguration) {
    return (field.customValues || []).map(opt => ({
      value: opt.code,
      label: language === 'fr' ? (opt.titleFrench || opt.code) : (opt.titleEnglish || opt.code),
      color: opt.color,
    }));
  }

  // Handle save from MultiSelectCell
  const handleMultiSelectSave = (selected: string[]) => {
    if (multiSelectIdx !== null) {
      setRows(rows.map((row, i) => i === multiSelectIdx ? { ...row, value: selected } : row));
      setMultiSelectIdx(null);
    }
  };
  const handleMultiSelectCancel = () => setMultiSelectIdx(null);

  const isAssignedToUserField = (field: FieldConfiguration) =>
    field.columnCode === 'AssignedToUser' || field.columnCode === 'AssignedToUser/Email';

  // Client field detection mirrors the inline editor (grid-cell.tsx) — the
  // canonical column code is `Client`, with a case-insensitive fallback for
  // alias columns. A Client cell is structurally a ClientDto-shaped object,
  // not a freeform string, so it gets its own picker + payload shape.
  const isClientField = (field: FieldConfiguration) =>
    (field.columnCode || '').toLowerCase() === 'client';

  const handleSubmit = () => {
    const hasField = rows.some(row => row.fieldConfigurationId);
    if (!hasField) {
      toast({
        title: t('noFieldsSelected') || 'No fields selected',
        description: t('pleaseSelectAtLeastOneField') || 'Please select at least one field.',
        variant: 'destructive',
      });
      return;
    }

    // Email format gate. EditableCell validates the INLINE path; without this, bulk edit is the wider
    // hole — email columns match none of the typed branches below and land in the catch-all, which
    // forwards the generic text input verbatim, so "n/a" across 40 selected rows persists on all 40.
    // Same helper and same translation key as the inline path, so the two can't drift on what counts
    // as an email. Runs before the confirm dialog opens, like the no-fields guard above.
    const badEmailRow = rows.find(r => {
      if (!r.fieldConfigurationId) return false;
      const field = fields.find(f => f.fieldConfigurationId === r.fieldConfigurationId);
      const raw = Array.isArray(r.value) ? r.value[0] : r.value;
      // Trimmed before validating, same as the inline path: " a@b.com " fails the pattern that the
      // trimmed value passes, and ConvertValue stores it trimmed regardless.
      const val = typeof raw === 'string' ? raw.trim() : raw;
      return !!field && isEmailField(field) && !isValidEmailFormat(val);
    });
    if (badEmailRow) {
      const field = fields.find(f => f.fieldConfigurationId === badEmailRow.fieldConfigurationId);
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        // The `t` prop here is narrowed to (key) => string, so interpolate manually rather than
        // passing the vars object useTranslation's own t supports.
        description: t('invalidEmailFormat').replace(
          '{field}',
          // The `language` prop is typed `string` here while getLocalizedField wants the 'en' | 'fr'
          // union; narrow rather than dropping the argument, or a FR user gets an EN column label.
          getLocalizedField(field, 'columnName', language as 'en' | 'fr')
        ),
      });
      return;
    }

    // fieldFormatting gate. Bulk edit had NO format check at all, so a value the grid rejected inline
    // could still be written to every selected row — the wider hole of the two, since one bad entry
    // lands on N records. Uses the same shared validator as the inline path so they cannot drift on
    // what counts as valid, including its skips for Date/DateTime display hints and codeValueSource
    // columns.
    const badFormatRow = rows.find(r => {
      if (!r.fieldConfigurationId) return false;
      const field = fields.find(f => f.fieldConfigurationId === r.fieldConfigurationId);
      const raw = Array.isArray(r.value) ? r.value[0] : r.value;
      const val = typeof raw === 'string' ? raw.trim() : raw;
      return !!field && !matchesFieldFormatting(field, val);
    });
    if (badFormatRow) {
      const field = fields.find(f => f.fieldConfigurationId === badFormatRow.fieldConfigurationId);
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: `Invalid format for ${getLocalizedField(field, 'columnName', language as 'en' | 'fr')}`,
      });
      return;
    }

    setShowConfirmDialog(true);
  };

  const performBulkUpdate = async () => {
    setIsSubmitting(true);

    // The payload build lives INSIDE the try. It used to run before it, so any throw here
    // (e.g. an ISO string reaching formatDateForSave) rejected the promise with nothing to
    // catch it: no toast, isSubmitting never flipped, and the confirm dialog just sat there —
    // the "Confirm does nothing" report. catch below already toasts; finally clears the flag.
    try {
      // Build updates array (V2: pass full item instead of just id)
      const updates = selectedItems.map(id => {
        const item = workOrders.find(wo => wo.id === id);
        if (!item) {
          console.error('Item not found:', id);
          return null;
        }

        const updateObj: Record<string, any> = {};
        rows.filter(r => r.fieldConfigurationId).forEach((r, rowIdx) => {
          const field = fields.find(f => f.fieldConfigurationId === r.fieldConfigurationId);
          if (!field) return;

          // Normalize AssignedToUser/Email → assignedToUser (the backend expects the nested object key)
          const rawFieldName = field.columnCode.charAt(0).toLowerCase() + field.columnCode.slice(1);
          const fieldName = isAssignedToUserField(field) ? 'assignedToUser' : rawFieldName;

          if (isAssignedToUserField(field)) {
            // Use the tracked user object from the search picker
            const selectedUser = selectedUserByRow[rows.indexOf(r)];
            if (selectedUser) {
              updateObj[fieldName] = { email: selectedUser.email, fullName: selectedUser.fullName, profileId: selectedUser.profileId };
            } else if (r.value === null || r.value === '') {
              updateObj[fieldName] = null;
            }
          } else if (isClientField(field)) {
            // Send the structured ClientDto shape — same payload the inline
            // grid-cell.tsx commitClient call uses. fboId is required server-
            // side; sending `null` clears the field.
            const selectedClient = selectedClientByRow[rows.indexOf(r)];
            if (selectedClient) {
              updateObj[fieldName] = {
                fboId: selectedClient.fboId,
                name: selectedClient.name,
                obCode: selectedClient.obCode,
              };
            } else if (r.value === null || r.value === '') {
              updateObj[fieldName] = null;
            }
          } else if (isTextListField(field)) {
            if (r.value === null || r.value === '' || (Array.isArray(r.value) && r.value.length === 0)) {
              updateObj[fieldName] = [];
            } else {
              updateObj[fieldName] = Array.isArray(r.value) ? r.value : [r.value];
            }
          } else if (isDateField(field)) {
            if (r.value === null || r.value === '') {
              updateObj[fieldName] = null;
            } else if (isDateTimeField(field)) {
              // DateTime fields: the picker is rendered with showTime and already emits an
              // ISO 8601 UTC string, so forward it verbatim — same contract the inline
              // editor uses (editable-cell.tsx DateTime branch skips formatDateForSave too).
              // Without this branch the ISO string fell into formatDateForSave below, which
              // splits on '-' and read "29T14:30:00.000Z" as the day → NaN → Invalid Date →
              // toISOString() threw RangeError. That throw happens while building the payload,
              // BEFORE the try block, so it surfaced as "Confirm does nothing": no toast, no
              // spinner, dialog just sat there.
              updateObj[fieldName] = typeof r.value === 'string' ? r.value : null;
            } else {
              updateObj[fieldName] = formatDateForSave(typeof r.value === 'string' ? r.value : null);
            }
          } else if (isNumberField(field)) {
            let val = Array.isArray(r.value) ? r.value[0] : r.value;
            if (val === null || val === '' || val === undefined) {
              updateObj[fieldName] = null;
            } else if (typeof val === 'string' && val.trim() !== '') {
              let parsed: number;
              if (field.columnTypeCode?.toLowerCase() === 'number') {
                parsed = parseInt(val, 10);
              } else {
                parsed = parseFloat(val);
              }
              if (!isNaN(parsed)) {
                updateObj[fieldName] = parsed;
              }
            }
          } else if (isBooleanField(field)) {
            // Boolean slots (BooleanField1-20): the editor stores the value as the
            // STRING "true"/"false" (from defaultBooleanOptions). Without this branch it
            // fell through to the default below and persisted the literal string "true",
            // which corrupts the record (CR/VMS Newtonsoft stores it verbatim; ATS STJ
            // JsonPatch 400s). Coerce to a real JS boolean — mirrors the inline
            // editable-cell fix so both edit paths emit a genuine boolean.
            const val = Array.isArray(r.value) ? r.value[0] : r.value;
            if (val === null || val === '' || val === undefined) {
              updateObj[fieldName] = null;
            } else {
              // String comparison alone. The dropped `val === true ||` clause was unreachable —
              // BulkEditRow.value is typed string | string[] | null and defaultBooleanOptions carries
              // the codes as the STRINGS 'true'/'false' — and it was also redundant, because
              // String(true).toLowerCase() === 'true' already covers a real boolean if one ever
              // arrives. It only produced a standing TS2367, which matters here: next.config sets
              // ignoreBuildErrors, so tsc output is the only type signal there is and a permanent
              // false positive in it hides real ones.
              updateObj[fieldName] = String(val).toLowerCase() === 'true';
            }
          } else {
            if (r.value === null || r.value === '' || r.value === undefined) {
              updateObj[fieldName] = null;
            } else {
              updateObj[fieldName] = r.value;
            }
          }
        });
        return { item, updates: updateObj };
      }).filter((u): u is { item: any; updates: Record<string, any> } => u !== null && Object.keys(u.updates).length > 0);

      if (updates.length === 0) {
        toast({
          title: t('noFieldsSelected') || 'No fields selected',
          description: t('pleaseSelectAtLeastOneField') || 'Please select at least one field and value.',
          variant: 'destructive',
        });
        return;
      }

      const result = await bulkUpdateReports(updates);
      const { successUpdateCount, failedUpdateCount, messages } = result;

      if (failedUpdateCount === 0) {
        toast({
          variant: "success",
          title: t('success') || 'Success',
          description: t('bulkUpdateSuccess')?.replace('{count}', String(successUpdateCount)) ||
                      `Successfully updated ${successUpdateCount} record${successUpdateCount !== 1 ? 's' : ''}.`,
        });
      } else if (successUpdateCount === 0) {
        const errorDetails = messages && messages.length > 0
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}`
          : '';
        toast({
          variant: "destructive",
          title: t('error') || 'Error',
          description: t('bulkUpdateFailed')?.replace('{count}', String(failedUpdateCount)).replace('{details}', errorDetails) ||
                      `Failed to update ${failedUpdateCount} record${failedUpdateCount !== 1 ? 's' : ''}${errorDetails}`,
        });
      } else {
        const errorDetails = messages && messages.length > 0
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}`
          : '';
        toast({
          variant: "default",
          title: t('partialSuccess') || 'Partial Success',
          description: t('bulkUpdatePartial')?.replace('{success}', String(successUpdateCount)).replace('{failed}', String(failedUpdateCount)).replace('{details}', errorDetails) ||
                      `Successfully updated ${successUpdateCount} record${successUpdateCount !== 1 ? 's' : ''}. ${failedUpdateCount} record${failedUpdateCount !== 1 ? 's' : ''} failed${errorDetails}`,
        });
      }

      trackWorkflowEvent('Click Tracking', {
        fieldsUpdated: rows.filter(r => r.fieldConfigurationId && r.value !== undefined).map(r => {
          const field = fields.find(f => f.fieldConfigurationId === r.fieldConfigurationId);
          return field ? field.columnCode : 'unknown';
        }),
        action: 'bulk_edit',
        successCount: successUpdateCount,
        failedCount: failedUpdateCount
      });

      // Only overlay optimistic values when the ENTIRE batch succeeded. The bulk response reports
      // aggregate success/failed counts but not which rows failed, so on a partial failure we can't
      // tell which rows to skip — and the pending overlay would hold a bogus value on the failed rows
      // across refreshes until the 20s timeout. When any row failed, skip the overlay and let the
      // refresh below show server truth. (Extend the API response with per-row success ids to overlay
      // the succeeded subset on partial failures.)
      if (successUpdateCount > 0 && failedUpdateCount === 0) {
        const optimisticUpdates = updates.map(u => ({ id: u.item.id, updates: u.updates }));
        applyOptimisticUpdates(optimisticUpdates);
      }

      // Refresh helper. VMS Email / VMS Request docs don't have a Stage column,
      // so the WorkOrder-shaped `Stage eq 'X'` base filter would reject every row
      // on refresh (the index has no such field, producing zero results). For
      // those entity families omit the base filter and just pass through the
      // current global + stage filters. Mirrors the construction in
      // move-to-modal.tsx so behaviour stays in lock-step.
      const doRefresh = () => {
        const stageConfig = configuration?.stageConfigurations?.find((s: any) => s.stageCode.toLowerCase() === currentStage?.toLowerCase());
        const currentStageFilters = stageFilters.filter(f => f.stageId === stageConfig?.stageId);
        const allFilters = [...globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean);
        // Shared gw-client-request-index entities (VmsEmail/VmsRequest/ClientRequest/
        // AtsPlacementRequest) have no Stage column — the WorkOrder-shaped `Stage eq 'X'`
        // base filter rejects every row. ClientRequest was missing here, so its post-bulk
        // refresh fired `Stage eq 'Request'` AND dropped the active filters. Keep this list in
        // lock-step with triggerDataFetch's isRequestEntityType.
        const isVmsEntity = entityType === 'VmsEmail' || entityType === 'VmsRequest'
          || entityType === 'AtsPlacementRequest' || entityType === 'ClientRequest';
        const baseFilter = isVmsEntity ? '' : `Stage eq '${currentStage}'`;
        const filterArray = isVmsEntity
          ? (allFilters.length > 0 ? [allFilters.join(' and ')] : [])
          : (allFilters.length > 0
              ? [`${baseFilter} and ${allFilters.join(' and ')}`]
              : [baseFilter]);
        const facetKeys = getFacetKeysFromConfiguration(configuration);
        refreshData(filterArray, undefined, facetKeys);
      };

      if (successUpdateCount > 0) {
        setShowConfirmDialog(false);
        resetRows();
        setSelectedItems([]);
        onClose();
        doRefresh();
        setTimeout(doRefresh, 500);
      } else {
        setShowConfirmDialog(false);
        doRefresh();
      }
    } catch (error: any) {
      setShowConfirmDialog(false);
      toast({
        title: t('bulkUpdateError') || 'Bulk update failed',
        description: error?.message || t('bulkUpdateErrorDesc') || 'An error occurred during bulk update.',
        variant: 'destructive',
      });

      const stageConfig = configuration?.stageConfigurations?.find((s: any) => s.stageCode.toLowerCase() === currentStage?.toLowerCase());
      const currentStageFilters = stageFilters.filter(f => f.stageId === stageConfig?.stageId);
      const allFilters = [...globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean);
      // Shared gw-client-request-index entities have no Stage column — omit the base
      // filter for those families (incl. ClientRequest, which was missing) so refresh
      // returns rows instead of failing the index query. Matches doRefresh + move-to-modal.
      const isVmsEntity = entityType === 'VmsEmail' || entityType === 'VmsRequest'
        || entityType === 'AtsPlacementRequest' || entityType === 'ClientRequest';
      const baseFilter = isVmsEntity ? '' : `Stage eq '${currentStage}'`;
      const filterArray = isVmsEntity
        ? (allFilters.length > 0 ? [allFilters.join(' and ')] : [])
        : (allFilters.length > 0
            ? [`${baseFilter} and ${allFilters.join(' and ')}`]
            : [baseFilter]);
      const facetKeys = getFacetKeysFromConfiguration(configuration);
      refreshData(filterArray, undefined, facetKeys);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get display label for a field value (for the confirmation dialog summary)
  const getValueLabel = (row: BulkEditRow, idx: number) => {
    const field = fields.find(f => f.fieldConfigurationId === row.fieldConfigurationId);
    if (!field) return '';
    if (isAssignedToUserField(field)) {
      const user = selectedUserByRow[idx];
      return user ? user.fullName : '(clear)';
    }
    if (isClientField(field)) {
      const client = selectedClientByRow[idx];
      return client ? client.name : '(clear)';
    }
    if (Array.isArray(row.value)) return row.value.join(', ');
    return row.value || '(clear)';
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { resetRows(); onClose(); } }}>
      <DialogContent className="w-[95vw] sm:w-[90vw] max-w-3xl min-w-[300px] sm:min-w-[700px] p-0" ref={modalContentRef}>
      <DropdownPortalCtx.Provider value={modalContentRef}>
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <DialogHeader className="p-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {t('editWorkItems') || 'Edit work items'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 mt-1">
            {t('bulkEditDescription')?.replace('{count}', String(selectedItems.length)) ||
              `Updating ${selectedItems.length} selected item${selectedItems.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-3 max-h-[60vh] overflow-y-auto transition-all duration-200 ease-in-out" style={{ scrollbarGutter: 'stable' }}>
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-3 items-center text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
            <span>{t('field') || 'Field'}</span>
            <span />
            <span>{t('value') || 'Value'}</span>
            <span className="w-8" />
          </div>

          {/* Field rows */}
          {rows.map((row, idx) => {
            const field = fields.find(f => f.fieldConfigurationId === row.fieldConfigurationId);
            const isAssignedTo = field && isAssignedToUserField(field);
            const isClient = field && isClientField(field);
            const isRemoving = removingKeys.has(rowKeys[idx]);
            return (
              <div
                key={rowKeys[idx] ?? idx}
                className={`grid grid-cols-[1fr_auto_1fr_auto] gap-3 items-center bg-gray-50/50 rounded-lg p-3 border border-gray-100 hover:border-gray-200 transition-all duration-200 ease-in-out ${isRemoving ? 'opacity-0 scale-95 -translate-y-1' : 'animate-in fade-in slide-in-from-top-1'}`}
              >
                {/* Field selector */}
                <FieldSelector
                  value={row.fieldConfigurationId}
                  options={fields.filter(f => !rows.some((r, i) => i !== idx && r.fieldConfigurationId === f.fieldConfigurationId))}
                  onChange={(fieldId) => handleFieldChange(idx, fieldId)}
                  placeholder={t('selectField') || 'Select field...'}
                  language={language}
                />

                {/* Equals sign */}
                <span className="text-gray-300 text-lg font-light">=</span>

                {/* Value editor */}
                <div className="min-w-0">
                  {field ? (
                    isAssignedTo ? (
                      <UserSearchPicker
                        value={typeof row.value === 'string' ? row.value : null}
                        onChange={(user) => {
                          if (user) {
                            handleValueChange(idx, user.email);
                            setSelectedUserByRow(prev => ({ ...prev, [idx]: user }));
                          } else {
                            handleValueChange(idx, null);
                            setSelectedUserByRow(prev => {
                              const next = { ...prev };
                              delete next[idx];
                              return next;
                            });
                          }
                        }}
                        placeholder={t('vmsSearchUsers')}
                        t={t}
                      />
                    ) : isClient ? (
                      <ClientSearchPicker
                        value={selectedClientByRow[idx] ?? null}
                        onChange={(client) => {
                          if (client) {
                            // Stash the structured pick under the row's
                            // string `value` slot purely so the row counts
                            // as "having a value" for submit validation —
                            // the real payload comes from selectedClientByRow.
                            handleValueChange(idx, client.name);
                            setSelectedClientByRow(prev => ({ ...prev, [idx]: client }));
                          } else {
                            handleValueChange(idx, null);
                            setSelectedClientByRow(prev => {
                              const next = { ...prev };
                              delete next[idx];
                              return next;
                            });
                          }
                        }}
                        placeholder={t('vmsSearchClient') || 'Search by client name...'}
                        t={t}
                      />
                    ) : isTextListField(field) ? (
                      <>
                        <button
                          type="button"
                          className="w-full h-9 px-3 text-sm text-left bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors truncate"
                          onClick={() => setMultiSelectIdx(idx)}
                        >
                          {Array.isArray(row.value) && row.value.length > 0
                            ? <span className="text-gray-900">{row.value.map((v: string) => {
                                const opt = getTextListOptions(field).find(o => o.value === v);
                                return opt ? opt.label : v;
                              }).join(', ')}</span>
                            : <span className="text-gray-400">{t('selectOptions') || 'Select options'}</span>}
                        </button>
                        {multiSelectIdx === idx && (
                          <MultiSelectCell
                            value={Array.isArray(row.value) ? row.value : []}
                            options={getTextListOptions(field)}
                            column={field}
                            rowId={'' + idx}
                            onSave={handleMultiSelectSave}
                            onCancel={handleMultiSelectCancel}
                          />
                        )}
                      </>
                    ) : isDateField(field) ? (
                      <ModernDatePicker
                        value={typeof row.value === 'string' ? row.value : (row.value === null ? '' : '')}
                        onChange={val => handleValueChange(idx, val || null)}
                        placeholder={t('selectDate') || 'Select date'}
                        portalContainer={modalContentRef.current}
                        // Bulk-edit a DateTime field — same UTC↔local
                        // round-trip the inline editor uses. The picker emits
                        // an ISO 8601 UTC string when showTime=true, which
                        // handleValueChange forwards verbatim to the bulk
                        // update endpoint. Other date columns keep the
                        // existing date-only YYYY-MM-DD behavior.
                        showTime={isDateTimeField(field)}
                      />
                    ) : isNumberField(field) ? (
                      <input
                        className="w-full h-9 px-3 text-sm bg-white border border-gray-200 rounded-lg outline-none focus:border-gray-300 transition-colors placeholder:text-gray-400"
                        type="number"
                        value={Array.isArray(row.value) ? (row.value[0] ?? '') : (row.value ?? '')}
                        onChange={e => handleValueChange(idx, e.target.value)}
                        placeholder={t('enterValue') || 'Enter value'}
                        step={field.columnTypeCode?.toLowerCase() === 'number' ? '1' : 'any'}
                      />
                    ) : isDropdownField(field) ? (
                      <CustomDropdownField
                        options={getDropdownOptions(field)}
                        selectedValues={Array.isArray(row.value) ? row.value : row.value ? [row.value] : []}
                        onChange={vals => handleValueChange(idx, vals.length > 0 ? vals[0] : null)}
                        placeholder={t('selectOption') || 'Select option'}
                        useColorCode={!!(field.customValues && field.customValues.length > 0)}
                        multiSelect={false}
                        portalContainer={modalContentRef.current}
                        allowClear={!!(field.customValues && field.customValues.length > 0)}
                      />
                    ) : isBooleanField(field) ? (
                      <CustomDropdownField
                        options={defaultBooleanOptions.map((opt, idx) => ({
                          value: opt.code,
                          label: language === 'fr' ? opt.titleFrench : opt.titleEnglish,
                          key: `${opt.code}-${idx}`
                        }))}
                        selectedValues={row.value == null ? [] : Array.isArray(row.value) ? row.value : [row.value]}
                        onChange={vals => handleValueChange(idx, vals.length > 0 ? vals[0] : null)}
                        placeholder={t('selectOption') || 'Select option'}
                        useColorCode={false}
                        multiSelect={false}
                        portalContainer={modalContentRef.current}
                        allowClear={true}
                      />
                    ) : (
                      <input
                        className="w-full h-9 px-3 text-sm bg-white border border-gray-200 rounded-lg outline-none focus:border-gray-300 transition-colors placeholder:text-gray-400"
                        value={Array.isArray(row.value) ? row.value.join(', ') : (row.value ?? '')}
                        onChange={e => handleValueChange(idx, e.target.value)}
                        placeholder={t('enterValue') || 'Enter value'}
                      />
                    )
                  ) : (
                    <input
                      className="w-full h-9 px-3 text-sm bg-gray-100 border border-gray-200 rounded-lg outline-none text-gray-400 cursor-not-allowed"
                      placeholder={t('selectFieldFirst') || 'Select a field first'}
                      disabled
                    />
                  )}
                </div>

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => handleRemoveRow(idx)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title={t('removeField') || 'Remove field'}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}

          {/* Add field button */}
          <button
            type="button"
            onClick={handleAddRow}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors w-fit"
          >
            <Plus size={15} /> {t('addNewField') || 'Add new field'}
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-2">
          <Button variant="elliptical-outline" type="button" onClick={() => { resetRows(); onClose(); }} disabled={isSubmitting}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button variant="elliptical" type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('processing') || 'Processing...' : t('applyChanges') || 'Apply Changes'}
          </Button>
        </div>
      </DropdownPortalCtx.Provider>
      </DialogContent>
    </Dialog>

    {/* Confirmation Dialog */}
    <Dialog open={showConfirmDialog} onOpenChange={() => setShowConfirmDialog(false)}>
      <DialogContent className="w-[95vw] sm:w-[90vw] max-w-md min-w-[280px] sm:min-w-[350px]">
        <DialogHeader>
          <DialogTitle>{t('confirmUpdate') || 'Confirm Update'}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-gray-600">
            {t('confirmUpdateMessage')?.replace('{count}', String(selectedItems.length)) || `You are about to update ${selectedItems.length} items. This action cannot be undone.`}
          </p>
          {rows.some(row => row.fieldConfigurationId && (row.value === null || row.value === '' || (Array.isArray(row.value) && row.value.length === 0))) && (
            <p className="text-amber-600 mt-3 text-sm">
              {t('someValuesEmpty') || 'Some values are empty and will clear the existing data.'}
            </p>
          )}
        </div>
        <DialogFooter className="flex justify-end gap-2">
          <Button
            variant="elliptical-outline"
            onClick={() => setShowConfirmDialog(false)}
            disabled={isSubmitting}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            variant="elliptical"
            onClick={performBulkUpdate}
            disabled={isSubmitting}
          >
            {isSubmitting ? (t('processing') || 'Processing...') : (t('confirm') || 'Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
